/**
 * Tool Runner — executes MCP tools against Confluence/Jira APIs.
 * Equivalent to run-tool.js but runs in the browser.
 * All auth tokens come from mcpAuth (sessionStorage), never hardcoded.
 *
 * AUTO-RETRY: On 401/403, automatically refreshes the token and retries once.
 */

import { mcpAuth, McpService } from './mcpAuth'
import { findToolByName, McpTool } from '../data/toolCatalog'
import { slack, parseSlackMessage, isSlackConnected } from './slack'
import { getGoogleToken } from './googleAuth'
import { notionCall } from './notion'
import { pushDebugLog } from '../hooks/useDebugLogs'

// Base URLs — these are public API Gateway endpoints, not secrets
const BASE_URLS: Record<string, string> = {
  confluence: 'https://x4smufuxj1.execute-api.us-east-1.amazonaws.com/dev/mcp',
  jira: 'https://h26174vmaa.execute-api.us-east-1.amazonaws.com',
}

// Google Workspace MCP — separate API Gateway
const GOOGLE_MCP_BASE_URL =
  'https://4xhkyip9jh.execute-api.us-east-1.amazonaws.com'

// Notion MCP — separate API Gateway
const NOTION_MCP_BASE_URL =
  'https://r9gafjgrkl.execute-api.us-east-1.amazonaws.com/dev'
export interface ToolResult {
  success: boolean
  tool: string
  service: McpService
  data: any
  error?: string
  latencyMs: number
}

/** Incrementing JSON-RPC request ID */
let jsonRpcId = 1

/**
 * Convert a catalog tool name (hyphens) to MCP server name (underscores).
 * e.g. 'search-issues' → 'search_issues', 'agile-get-boards' → 'agile_get_boards'
 */
function toMcpToolName(catalogName: string): string {
  return catalogName.replace(/-/g, '_')
}

/**
 * Execute a single HTTP request for a tool.
 *
 * Both Jira and Confluence use MCP JSON-RPC protocol:
 *   POST { jsonrpc: "2.0", method: "tools/call", params: { name, arguments } }
 *   The server returns { result: { content: [{ type: "text", text: "..." }] } }
 *
 * The claimId (tenant_id) is extracted from the JWT IdToken server-side —
 * same Cognito pool for both services.
 */
async function executeRequest(
  tool: McpTool,
  args: Record<string, any>,
  token: string,
): Promise<Response> {
  const baseUrl = BASE_URLS[tool.service]
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  // ── Both Jira & Confluence: JSON-RPC at /mcp ──
  if (tool.service === 'jira' || tool.service === 'confluence') {
    const mcpName = toMcpToolName(tool.name)
    const body = {
      jsonrpc: '2.0',
      id: jsonRpcId++,
      method: 'tools/call',
      params: {
        name: mcpName,
        arguments: args,
      },
    }
    // Jira: baseUrl + /mcp | Confluence: baseUrl already ends in /mcp
    const url = tool.service === 'jira' ? `${baseUrl}/mcp` : baseUrl
    console.log(`[ToolRunner] JSON-RPC ${url} → ${mcpName}`, args)
    return fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
  }

  // ── Fallback: REST paths (other services) ──
  let url = `${baseUrl}${tool.path}`
  let fetchOptions: RequestInit = {
    method: tool.method,
    headers,
  }
  if (tool.paramsIn === 'query' && Object.keys(args).length > 0) {
    const params = new URLSearchParams()
    Object.entries(args).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params.set(k, String(v))
      }
    })
    url += `?${params.toString()}`
  } else if (
    tool.paramsIn === 'body' &&
    (tool.method === 'POST' || tool.method === 'PUT')
  ) {
    fetchOptions.body = JSON.stringify(args)
  }
  console.log(`[ToolRunner] ${tool.method} ${url}`)
  return fetch(url, fetchOptions)
}

/**
 * Execute a tool by name with given arguments.
 * Handles auth, URL construction, error handling, and AUTO-RETRY on 401/403.
 * Automatically logs every call to the Global Debug Console.
 */
export async function runTool(
  toolName: string,
  args: Record<string, any> = {},
): Promise<ToolResult> {
  const result = await _runToolImpl(toolName, args)

  // Push to global debug console
  try {
    pushDebugLog({
      source: result.service || 'system',
      tool: result.tool,
      params: args,
      rawResponse: result.data,
      processedData: result.data,
      success: result.success,
      error: result.error,
      durationMs: result.latencyMs,
    })
  } catch {
    // Never let debug logging break the actual tool execution
  }
  return result
}

/**
 * Internal implementation — all the original runTool logic.
 */
async function _runToolImpl(
  toolName: string,
  args: Record<string, any> = {},
): Promise<ToolResult> {
  const start = performance.now()
  const tool = findToolByName(toolName)
  if (!tool) {
    return {
      success: false,
      tool: toolName,
      service: 'jira',
      data: null,
      error: `Tool "${toolName}" no encontrado en el catálogo.`,
      latencyMs: 0,
    }
  }

  // ═══════════════════════════════════════════════
  // SLACK — execute directly via slack service (no API Gateway)
  // ═══════════════════════════════════════════════
  if (tool.service === 'slack') {
    try {
      if (!isSlackConnected()) {
        return {
          success: false,
          tool: toolName,
          service: 'slack',
          data: null,
          error: 'Slack no está conectado. Configurá el token en Settings.',
          latencyMs: Math.round(performance.now() - start),
        }
      }
      let data: any
      switch (toolName) {
        case 'slack_get_channels': {
          const channels = await slack.getChannels()
          data = {
            total: channels.length,
            channels: channels.map((c) => ({
              id: c.id,
              name: c.name,
              is_private: c.is_private,
              num_members: c.num_members,
            })),
          }
          break
        }
        case 'slack_read_channel': {
          const limit = args.limit || 30
          const messages = await slack.getChannelHistory(args.channel_id, limit)
          // Pre-fetch users for display names
          const userIds = [
            ...new Set(messages.map((m) => m.user).filter(Boolean)),
          ]
          const userMap: Record<string, string> = {}
          for (const uid of userIds) {
            const user = await slack.getUserInfo(uid as string)
            if (user) {
              userMap[uid as string] =
                user.profile?.display_name || user.real_name || user.name
            }
          }
          data = {
            channel_id: args.channel_id,
            total: messages.length,
            messages: messages.reverse().map((m) => ({
              user: m.user ? userMap[m.user] || m.user : 'bot',
              text: parseSlackMessage(m.text),
              timestamp: new Date(parseFloat(m.ts) * 1000).toLocaleString(
                'es-AR',
              ),
              is_bot: !!m.bot_id,
              reply_count: m.reply_count || 0,
            })),
          }
          break
        }
        case 'slack_send_message': {
          const msg = await slack.sendMessage(args.channel_id, args.text)
          data = {
            success: true,
            ts: msg.ts,
            text: msg.text,
          }
          break
        }
        case 'slack_get_users': {
          const users = await slack.getUsers()
          data = {
            total: users.length,
            users: users
              .filter((u) => !u.is_bot)
              .map((u) => ({
                id: u.id,
                name: u.name,
                real_name: u.real_name,
                display_name: u.profile?.display_name,
                status: u.profile?.status_text || '',
              })),
          }
          break
        }
        case 'slack_get_channel_by_name': {
          const channels = await slack.getChannels()
          const found = channels.find(
            (c) => c.name.toLowerCase() === args.channel_name.toLowerCase(),
          )
          if (found) {
            data = {
              id: found.id,
              name: found.name,
              is_private: found.is_private,
              num_members: found.num_members,
            }
          } else {
            data = {
              error: `Canal "${args.channel_name}" no encontrado`,
            }
          }
          break
        }
        default:
          return {
            success: false,
            tool: toolName,
            service: 'slack',
            data: null,
            error: `Tool de Slack "${toolName}" no implementada.`,
            latencyMs: Math.round(performance.now() - start),
          }
      }
      return {
        success: true,
        tool: toolName,
        service: 'slack',
        data,
        latencyMs: Math.round(performance.now() - start),
      }
    } catch (err: any) {
      return {
        success: false,
        tool: toolName,
        service: 'slack',
        data: null,
        error: err.message || 'Error ejecutando tool de Slack',
        latencyMs: Math.round(performance.now() - start),
      }
    }
  }

  // ═══════════════════════════════════════════════
  // GOOGLE — execute directly via Google APIs
  // ═══════════════════════════════════════════════
  if (tool.service === 'google') {
    try {
      const googleToken = getGoogleToken()
      if (!googleToken) {
        return {
          success: false,
          tool: toolName,
          service: 'google',
          data: null,
          error:
            'Google no está conectado. Conectá tu cuenta desde el panel de Conectores.',
          latencyMs: Math.round(performance.now() - start),
        }
      }
      let data: any
      switch (toolName) {
        case 'google_create_doc': {
          // Step 1: Create the document
          const createRes = await fetch(
            'https://docs.googleapis.com/v1/documents',
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${googleToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                title: args.title || 'Nuevo Documento',
              }),
            },
          )
          if (!createRes.ok) {
            const err = await createRes.json().catch(() => ({}))
            throw new Error(
              err?.error?.message ||
                `Google Docs API error: ${createRes.status}`,
            )
          }
          const doc = await createRes.json()

          // Step 2: Insert content if provided
          if (args.content) {
            const content = String(args.content)
            await fetch(
              `https://docs.googleapis.com/v1/documents/${doc.documentId}:batchUpdate`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${googleToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  requests: [
                    {
                      insertText: {
                        location: {
                          index: 1,
                        },
                        text: content,
                      },
                    },
                  ],
                }),
              },
            )
          }
          data = {
            documentId: doc.documentId,
            title: doc.title,
            url: `https://docs.google.com/document/d/${doc.documentId}/edit`,
            message: `✅ Documento "${doc.title}" creado exitosamente.`,
          }
          break
        }
        case 'google_create_sheet': {
          // Create spreadsheet
          const sheetBody: any = {
            properties: {
              title: args.title || 'Nuevo Spreadsheet',
            },
          }

          // Add headers and data if provided
          const rowData: any[][] = []
          if (args.headers) {
            const headers = Array.isArray(args.headers)
              ? args.headers
              : String(args.headers).split(',')
            rowData.push(headers)
          }
          if (args.rows) {
            try {
              const parsed =
                typeof args.rows === 'string'
                  ? JSON.parse(args.rows)
                  : args.rows
              if (Array.isArray(parsed)) {
                rowData.push(...parsed)
              }
            } catch {
              // If rows can't be parsed, ignore
            }
          }
          const createSheetRes = await fetch(
            'https://sheets.googleapis.com/v4/spreadsheets',
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${googleToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(sheetBody),
            },
          )
          if (!createSheetRes.ok) {
            const err = await createSheetRes.json().catch(() => ({}))
            throw new Error(
              err?.error?.message ||
                `Google Sheets API error: ${createSheetRes.status}`,
            )
          }
          const sheet = await createSheetRes.json()

          // Populate data if we have rows
          if (rowData.length > 0) {
            const range = `Sheet1!A1:${String.fromCharCode(64 + (rowData[0]?.length || 1))}${rowData.length}`
            await fetch(
              `https://sheets.googleapis.com/v4/spreadsheets/${sheet.spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=RAW`,
              {
                method: 'PUT',
                headers: {
                  Authorization: `Bearer ${googleToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  values: rowData,
                }),
              },
            )
          }
          data = {
            spreadsheetId: sheet.spreadsheetId,
            title: sheet.properties.title,
            url: sheet.spreadsheetUrl,
            message: `✅ Spreadsheet "${sheet.properties.title}" creado exitosamente.`,
            rowCount: rowData.length,
          }
          break
        }
        case 'google_create_slide': {
          // Create presentation
          const createSlideRes = await fetch(
            'https://slides.googleapis.com/v1/presentations',
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${googleToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                title: args.title || 'Nueva Presentación',
              }),
            },
          )
          if (!createSlideRes.ok) {
            const err = await createSlideRes.json().catch(() => ({}))
            throw new Error(
              err?.error?.message ||
                `Google Slides API error: ${createSlideRes.status}`,
            )
          }
          const presentation = await createSlideRes.json()

          // Add slides if provided
          if (args.slides) {
            try {
              const slides =
                typeof args.slides === 'string'
                  ? JSON.parse(args.slides)
                  : args.slides
              if (Array.isArray(slides) && slides.length > 0) {
                const requests: any[] = []
                slides.forEach(
                  (
                    slide: {
                      title?: string
                      body?: string
                    },
                    idx: number,
                  ) => {
                    const slideId = `slide_${idx + 1}`
                    requests.push({
                      createSlide: {
                        objectId: slideId,
                        insertionIndex: idx + 1,
                        slideLayoutReference: {
                          predefinedLayout: 'TITLE_AND_BODY',
                        },
                      },
                    })
                  },
                )
                // Create all slides first
                await fetch(
                  `https://slides.googleapis.com/v1/presentations/${presentation.presentationId}:batchUpdate`,
                  {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${googleToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      requests,
                    }),
                  },
                )
              }
            } catch {
              // If slides can't be parsed, just create the presentation
            }
          }
          data = {
            presentationId: presentation.presentationId,
            title: presentation.title,
            url: `https://docs.google.com/presentation/d/${presentation.presentationId}/edit`,
            message: `✅ Presentación "${presentation.title}" creada exitosamente.`,
          }
          break
        }
        default:
          return {
            success: false,
            tool: toolName,
            service: 'google',
            data: null,
            error: `Tool de Google "${toolName}" no implementada.`,
            latencyMs: Math.round(performance.now() - start),
          }
      }
      return {
        success: true,
        tool: toolName,
        service: 'google',
        data,
        latencyMs: Math.round(performance.now() - start),
      }
    } catch (err: any) {
      return {
        success: false,
        tool: toolName,
        service: 'google',
        data: null,
        error: err.message || 'Error ejecutando tool de Google',
        latencyMs: Math.round(performance.now() - start),
      }
    }
  }

  // ═══════════════════════════════════════════════
  // GOOGLE MCP — execute via Google Workspace MCP API Gateway
  // ═══════════════════════════════════════════════
  if (tool.service === 'google-mcp' || toolName === 'execute_mcp_tool') {
    try {
      // For execute_mcp_tool, extract the real tool name and params
      const actualToolName =
        toolName === 'execute_mcp_tool' ? args.tool_name : toolName
      const actualParams =
        toolName === 'execute_mcp_tool'
          ? typeof args.params === 'string'
            ? JSON.parse(args.params)
            : args.params || {}
          : args
      if (!actualToolName) {
        return {
          success: false,
          tool: toolName,
          service: 'google-mcp',
          data: null,
          error: 'tool_name es requerido para execute_mcp_tool',
          latencyMs: Math.round(performance.now() - start),
        }
      }

      // Get tenant_id from localStorage (user's Google email)
      const tenantId = localStorage.getItem('vsm_google_mcp_tenant_id')
      if (!tenantId) {
        return {
          success: false,
          tool: actualToolName,
          service: 'google-mcp',
          data: null,
          error:
            'Google Workspace MCP no está conectado. Configurá tu cuenta desde el panel de Conectores.',
          latencyMs: Math.round(performance.now() - start),
        }
      }

      // Call the Google MCP API Gateway
      const response = await fetch(
        `${GOOGLE_MCP_BASE_URL}/tool/${actualToolName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            params: {
              tenant_id: tenantId,
              ...actualParams,
            },
          }),
        },
      )
      const latencyMs = Math.round(performance.now() - start)
      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        return {
          success: false,
          tool: actualToolName,
          service: 'google-mcp',
          data: null,
          error: `Google MCP error ${response.status}: ${errorText}`,
          latencyMs,
        }
      }
      const data = await response.json()
      return {
        success: true,
        tool: actualToolName,
        service: 'google-mcp',
        data,
        latencyMs,
      }
    } catch (err: any) {
      return {
        success: false,
        tool: toolName,
        service: 'google-mcp',
        data: null,
        error: err.message || 'Error ejecutando tool de Google MCP',
        latencyMs: Math.round(performance.now() - start),
      }
    }
  }

  // ═══════════════════════════════════════════════
  // NOTION MCP — execute via Notion MCP API Gateway
  // ═══════════════════════════════════════════════
  if (tool.service === 'notion') {
    try {
      const result = await notionCall(toolName, args)
      const latencyMs = Math.round(performance.now() - start)
      if (!result.success) {
        return {
          success: false,
          tool: toolName,
          service: 'notion',
          data: null,
          error: result.error || 'Error ejecutando tool de Notion',
          latencyMs,
        }
      }
      return {
        success: true,
        tool: toolName,
        service: 'notion',
        data: result.data,
        latencyMs,
      }
    } catch (err: any) {
      return {
        success: false,
        tool: toolName,
        service: 'notion',
        data: null,
        error: err.message || 'Error ejecutando tool de Notion',
        latencyMs: Math.round(performance.now() - start),
      }
    }
  }

  // ═══════════════════════════════════════════════
  // CONFLUENCE / JIRA — execute via API Gateway
  // ═══════════════════════════════════════════════
  try {
    // Get auth token
    let token = await mcpAuth.getToken(tool.service as 'confluence' | 'jira')
    let response = await executeRequest(tool, args, token)

    // ═══════════════════════════════════════════════
    // AUTO-RETRY on 401/403 — refresh token and retry once
    // ═══════════════════════════════════════════════
    if (response.status === 401 || response.status === 403) {
      console.warn(
        `[ToolRunner] ${toolName} got ${response.status}, auto-refreshing ${tool.service} token...`,
      )

      // Clear the expired token
      mcpAuth.clearTokenForService(tool.service)
      try {
        // Re-authenticate (getToken will re-auth since we cleared the cache)
        token = await mcpAuth.getToken(tool.service)
        console.log(
          `[ToolRunner] ${tool.service} token refreshed ✓, retrying ${toolName}...`,
        )

        // Retry the request with the new token
        response = await executeRequest(tool, args, token)

        // If still 401/403 after retry, credentials are wrong
        if (response.status === 401 || response.status === 403) {
          const latencyMs = Math.round(performance.now() - start)
          return {
            success: false,
            tool: toolName,
            service: tool.service,
            data: null,
            error: `Autenticación fallida para ${tool.service} después de reintentar. Verificá las credenciales en Settings.`,
            latencyMs,
          }
        }
      } catch (refreshErr: any) {
        const latencyMs = Math.round(performance.now() - start)
        return {
          success: false,
          tool: toolName,
          service: tool.service,
          data: null,
          error: `No se pudo refrescar el token de ${tool.service}: ${refreshErr.message}. Verificá las credenciales en Settings.`,
          latencyMs,
        }
      }
    }
    const latencyMs = Math.round(performance.now() - start)
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText)
      return {
        success: false,
        tool: toolName,
        service: tool.service,
        data: null,
        error: `Error ${response.status}: ${errorText}`,
        latencyMs,
      }
    }
    const rawData = await response.json()

    // ═══════════════════════════════════════════════
    // Parse JSON-RPC response for Jira & Confluence MCP
    // Server returns: { jsonrpc: "2.0", id, result: { content: [{ type: "text", text: "..." }], isError: bool } }
    // We extract the actual data from result.content[0].text (JSON string).
    // If result.isError is true, the text contains an error message (HTTP 200 but tool failed).
    // ═══════════════════════════════════════════════
    let data: any
    if (
      (tool.service === 'jira' || tool.service === 'confluence') &&
      rawData?.jsonrpc === '2.0'
    ) {
      // Check for JSON-RPC level error (transport/protocol error)
      if (rawData.error) {
        return {
          success: false,
          tool: toolName,
          service: tool.service,
          data: null,
          error: rawData.error.message || JSON.stringify(rawData.error),
          latencyMs,
        }
      }

      // Extract text from content blocks
      const content = rawData.result?.content
      const isError = rawData.result?.isError === true
      let parsedText: any = null
      let rawText: string = ''
      if (Array.isArray(content) && content.length > 0) {
        const textBlock = content.find((c: any) => c.type === 'text')
        if (textBlock?.text) {
          rawText = textBlock.text
          try {
            parsedText = JSON.parse(textBlock.text)
          } catch {
            // Not valid JSON — keep as string
            parsedText = null
          }
        }
      }

      // If the MCP tool reported an error (isError: true)
      if (isError) {
        return {
          success: false,
          tool: toolName,
          service: tool.service,
          data: null,
          error: rawText || 'Error en la herramienta MCP',
          latencyMs,
        }
      }

      // Success — use parsed JSON or fallback
      data =
        parsedText ??
        (rawText
          ? {
              message: rawText,
            }
          : rawData.result || rawData)
    } else {
      // Non-JSON-RPC response (other services) — use as-is
      data = rawData
    }
    return {
      success: true,
      tool: toolName,
      service: tool.service,
      data,
      latencyMs,
    }
  } catch (err: any) {
    const latencyMs = Math.round(performance.now() - start)
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      return {
        success: false,
        tool: toolName,
        service: tool.service,
        data: null,
        error: `Error de conexión con ${tool.service}. Posible problema de CORS o red.`,
        latencyMs,
      }
    }
    return {
      success: false,
      tool: toolName,
      service: tool.service,
      data: null,
      error: err.message || 'Error desconocido',
      latencyMs,
    }
  }
}

/**
 * List available tools from a service.
 * Both Jira and Confluence use JSON-RPC tools/list at /mcp.
 */
export async function listRemoteTools(
  service: McpService,
): Promise<ToolResult> {
  const start = performance.now()
  try {
    const token = await mcpAuth.getToken(service)
    const baseUrl = BASE_URLS[service]

    // Both Jira & Confluence: JSON-RPC tools/list
    const url = service === 'jira' ? `${baseUrl}/mcp` : baseUrl
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: jsonRpcId++,
        method: 'tools/list',
        params: {},
      }),
    })
    const raw = await response.json()
    const data = raw?.result?.tools || raw?.result || raw
    return {
      success: true,
      tool: 'list-tools',
      service,
      data,
      latencyMs: Math.round(performance.now() - start),
    }
  } catch (err: any) {
    return {
      success: false,
      tool: 'list-tools',
      service,
      data: null,
      error: err.message,
      latencyMs: Math.round(performance.now() - start),
    }
  }
}
